import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ZombieGameSimulator = () => {
  const [mode, setMode] = useState(1); // 1: 不变僵尸, 2: 变僵尸
  const [totalPeople, setTotalPeople] = useState(20);
  const [people, setPeople] = useState([]);
  const [day, setDay] = useState(0);
  const [selectedAttacks, setSelectedAttacks] = useState({}); // {personId: attackCount}
  const [dailyStats, setDailyStats] = useState([]); // 每日统计数据
  const [historyLog, setHistoryLog] = useState([]); // 历史记录
  const [chartMode, setChartMode] = useState("line"); // 'line', 'bar', 'stack'
  const [visibleLines, setVisibleLines] = useState({
    // 控制图表中显示的数据线
    zombies: true,
    healthy: true,
    attacked1: true,
    infected: true,
  });
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState("");

  // 初始化游戏
  const initializeGame = () => {
    const initialPeople = [];
    // 第一个是僵尸
    initialPeople.push({ id: 0, status: "zombie", attackCount: 0 });
    // 其他是健康人
    for (let i = 1; i <= totalPeople; i++) {
      initialPeople.push({ id: i, status: "healthy", attackCount: 0 });
    }
    setPeople(initialPeople);
    setDay(0);
    setSelectedAttacks({});
    setGameStarted(true);
    setGameOver(false);
    setMessage("游戏开始！选择要攻击的目标。");

    // 初始化统计数据
    const initialStats = {
      day: 0,
      zombies: 1,
      healthy: totalPeople,
      attacked1: 0,
      infected: 0,
    };
    setDailyStats([initialStats]);
    setHistoryLog([
      {
        day: 0,
        action: "游戏开始",
        details: `1个僵尸，${totalPeople}个健康人`,
      },
    ]);
  };

  // 重置游戏
  const resetGame = () => {
    setPeople([]);
    setDay(0);
    setSelectedAttacks({});
    setGameStarted(false);
    setGameOver(false);
    setMessage("");
    setDailyStats([]);
    setVisibleLines({
      zombies: true,
      healthy: true,
      attacked1: true,
      infected: true,
    });
  };

  // 计算当前僵尸数量和每天攻击次数
  const getZombieStats = () => {
    const zombieCount = people.filter((p) => p.status === "zombie").length;
    const attacksPerDay = mode === 1 ? 2 : zombieCount * 2;
    return { zombieCount, attacksPerDay };
  };

  // 检查是否还有可攻击的目标
  const getAttackableTargets = () => {
    return people.filter((p) => {
      if (p.status === "zombie") return false;
      if (mode === 1 && p.status === "infected") return false;
      // 检查是否还能被攻击（最多2次）
      const selectedCount = selectedAttacks[p.id] || 0;
      return p.attackCount + selectedCount < 2;
    });
  };

  // 选择攻击目标
  const toggleTarget = (personId) => {
    if (gameOver) return;

    const person = people.find((p) => p.id === personId);
    if (person.status === "zombie") return; // 不能攻击僵尸
    if (mode === 1 && person.status === "infected") return; // 模式1中不能攻击已感染的人

    const { attacksPerDay } = getZombieStats();
    const currentTotalAttacks = Object.values(selectedAttacks).reduce(
      (sum, count) => sum + count,
      0
    );
    const currentPersonAttacks = selectedAttacks[personId] || 0;
    const currentPersonTotalAttacks = person.attackCount + currentPersonAttacks;

    // 检查是否还能增加攻击
    if (currentTotalAttacks < attacksPerDay && currentPersonTotalAttacks < 2) {
      setSelectedAttacks((prev) => ({
        ...prev,
        [personId]: (prev[personId] || 0) + 1,
      }));
    }
  };

  // 移除对某人的一次攻击
  const removeAttack = (personId) => {
    setSelectedAttacks((prev) => {
      const newAttacks = { ...prev };
      if (newAttacks[personId] > 1) {
        newAttacks[personId]--;
      } else {
        delete newAttacks[personId];
      }
      return newAttacks;
    });
  };

  // 执行攻击
  const executeAttacks = () => {
    const { attacksPerDay } = getZombieStats();
    const totalSelectedAttacks = Object.values(selectedAttacks).reduce(
      (sum, count) => sum + count,
      0
    );
    const attackableTargets = getAttackableTargets();

    if (totalSelectedAttacks === 0) {
      setMessage("请选择攻击目标！");
      return;
    }

    // 检查是否还有可攻击的目标，如果没有则可以执行；如果有则必须用完攻击次数
    if (attackableTargets.length > 0 && totalSelectedAttacks < attacksPerDay) {
      setMessage(
        `必须用完所有攻击次数！还需要选择 ${
          attacksPerDay - totalSelectedAttacks
        } 次攻击。`
      );
      return;
    }

    const newPeople = people.map((person) => ({ ...person }));
    let newZombies = 0;
    const attackDetails = [];

    // 执行攻击
    Object.entries(selectedAttacks).forEach(([id, attacks]) => {
      const personIndex = newPeople.findIndex((p) => p.id === parseInt(id));
      if (personIndex !== -1) {
        const originalStatus = newPeople[personIndex].status;
        newPeople[personIndex].attackCount += attacks;
        attackDetails.push(`攻击${id}号${attacks}次`);

        if (
          newPeople[personIndex].attackCount >= 2 &&
          newPeople[personIndex].status !== "zombie"
        ) {
          if (mode === 2) {
            newPeople[personIndex].status = "zombie";
            newZombies++;
          } else {
            newPeople[personIndex].status = "infected";
          }
        } else if (newPeople[personIndex].attackCount === 1) {
          newPeople[personIndex].status = "attacked1";
        }
      }
    });

    // 更新统计数据
    const newStats = {
      day: day + 1,
      zombies: newPeople.filter((p) => p.status === "zombie").length,
      healthy: newPeople.filter((p) => p.status === "healthy").length,
      attacked1: newPeople.filter((p) => p.status === "attacked1").length,
      infected: newPeople.filter((p) => p.status === "infected").length,
    };

    setPeople(newPeople);
    setDay((prev) => prev + 1);
    setSelectedAttacks({});
    setDailyStats((prev) => [...prev, newStats]);

    // 添加历史记录
    const logEntry = {
      day: day + 1,
      action: "执行攻击",
      details: attackDetails.join("，"),
      result:
        mode === 2 && newZombies > 0 ? `新增${newZombies}个僵尸` : `攻击完成`,
    };
    setHistoryLog((prev) => [...prev, logEntry]);

    // 检查游戏结束
    const healthyCount = newPeople.filter((p) => p.status === "healthy").length;
    const attacked1Count = newPeople.filter(
      (p) => p.status === "attacked1"
    ).length;

    if (healthyCount === 0 && attacked1Count === 0) {
      setGameOver(true);
      const finalMessage = `🎉 游戏结束！\n\n📊 最终结果：\n• 用时：${
        day + 1
      } 天\n• 游戏模式：${
        mode === 1 ? "感染者不变僵尸" : "感染者变僵尸"
      }\n• 总人数：${totalPeople + 1} 人\n• 最终僵尸数：${
        newStats.zombies
      } 个\n${mode === 1 ? `• 感染者数：${newStats.infected} 个` : ""}\n\n${
        mode === 2
          ? day + 1 <= 10
            ? "🚀 策略效率很高！"
            : "🐌 可以尝试更快的策略"
          : ""
      }`;
      setMessage(finalMessage);
      setHistoryLog((prev) => [
        ...prev,
        {
          day: day + 1,
          action: "游戏结束",
          details: `全部感染完成，用时${day + 1}天`,
        },
      ]);
    } else {
      const { zombieCount } = getZombieStats();
      if (mode === 2 && newZombies > 0) {
        setMessage(
          `第 ${day + 1} 天结束。新增 ${newZombies} 个僵尸，当前共 ${
            zombieCount + newZombies
          } 个僵尸。`
        );
      } else {
        setMessage(
          `第 ${day + 1} 天结束。剩余 ${
            healthyCount + attacked1Count
          } 人未完全感染。`
        );
      }
    }
  };

  // 获取人员显示的CSS类
  const getPersonClass = (person) => {
    let baseClass =
      "w-8 h-8 rounded-full border-2 cursor-pointer flex items-center justify-center text-xs font-bold ";

    switch (person.status) {
      case "zombie":
        return baseClass + "bg-green-700 text-white border-green-900"; // 深绿色
      case "attacked1":
        return baseClass + "bg-green-200 text-black border-green-400"; // 浅绿色
      case "infected":
        return baseClass + "bg-purple-500 text-white border-purple-700"; // 紫色
      default: // healthy
        return baseClass + "bg-white text-black border-blue-500"; // 白色背景，蓝色边框
    }
  };

  // 获取选中状态的边框
  const getSelectedBorder = (personId) => {
    const attackCount = selectedAttacks[personId] || 0;
    if (attackCount === 0) return "";
    if (attackCount === 1) return "ring-4 ring-blue-400";
    return "ring-4 ring-blue-600"; // 被选择攻击2次时用更深的蓝色
  };

  // 切换数据线显示
  const toggleDataLine = (lineType) => {
    setVisibleLines((prev) => ({
      ...prev,
      [lineType]: !prev[lineType],
    }));
  };

  // 渲染圆圈柱状图
  const renderCircleBarChart = () => {
    const maxHeight = 15; // 限制最大高度，避免过高

    return (
      <div className="bg-white p-4 rounded border">
        <div
          className="overflow-x-auto overflow-y-hidden"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e0 #f7fafc",
          }}
        >
          <div
            className="flex justify-start items-end space-x-6 pb-4"
            style={{
              minHeight: `${maxHeight * 20 + 100}px`,
              minWidth: `${Math.max(dailyStats.length * 120, 600)}px`, // 确保有足够宽度
              width: "max-content", // 让容器适应内容宽度
            }}
          >
            {dailyStats.map((stat, dayIndex) => (
              <div
                key={stat.day}
                className="flex flex-col items-center flex-shrink-0"
                style={{ minWidth: "100px" }}
              >
                {/* 天数标签 */}
                <div className="text-xs font-medium text-gray-600 mb-2">
                  第{stat.day}天
                </div>

                {/* 圆圈柱容器 */}
                <div className="flex space-x-2 items-end justify-center">
                  {/* 僵尸柱 */}
                  {visibleLines.zombies && (
                    <div className="flex flex-col items-center">
                      <div
                        className="flex flex-col-reverse space-y-reverse space-y-1"
                        style={{ minHeight: "20px" }}
                      >
                        {stat.zombies > maxHeight ? (
                          <>
                            {/* 显示前几个圆圈 */}
                            {Array.from({ length: maxHeight - 1 }, (_, i) => (
                              <div
                                key={`zombie-${i}`}
                                className="w-4 h-4 bg-green-700 border border-green-900 rounded-full"
                              />
                            ))}
                            {/* 省略号指示 */}
                            <div className="w-4 h-4 flex items-center justify-center text-xs text-green-700 font-bold">
                              ⋯
                            </div>
                          </>
                        ) : (
                          Array.from({ length: stat.zombies }, (_, i) => (
                            <div
                              key={`zombie-${i}`}
                              className="w-4 h-4 bg-green-700 border border-green-900 rounded-full"
                            />
                          ))
                        )}
                      </div>
                      <div className="text-xs text-green-700 mt-2 font-medium">
                        {stat.zombies}
                      </div>
                      <div className="text-xs text-gray-500">僵尸</div>
                    </div>
                  )}

                  {/* 健康人柱 - 统一使用白色背景+蓝色边框 */}
                  {visibleLines.healthy && (
                    <div className="flex flex-col items-center">
                      <div
                        className="flex flex-col-reverse space-y-reverse space-y-1"
                        style={{ minHeight: "20px" }}
                      >
                        {stat.healthy > maxHeight ? (
                          <>
                            {Array.from({ length: maxHeight - 1 }, (_, i) => (
                              <div
                                key={`healthy-${i}`}
                                className="w-4 h-4 bg-white border border-blue-500 rounded-full"
                              />
                            ))}
                            <div className="w-4 h-4 flex items-center justify-center text-xs text-blue-500 font-bold">
                              ⋯
                            </div>
                          </>
                        ) : (
                          Array.from({ length: stat.healthy }, (_, i) => (
                            <div
                              key={`healthy-${i}`}
                              className="w-4 h-4 bg-white border border-blue-500 rounded-full"
                            />
                          ))
                        )}
                      </div>
                      <div className="text-xs text-blue-500 mt-2 font-medium">
                        {stat.healthy}
                      </div>
                      <div className="text-xs text-gray-500">健康</div>
                    </div>
                  )}

                  {/* 被攻击1次柱 */}
                  {visibleLines.attacked1 && (
                    <div className="flex flex-col items-center">
                      <div
                        className="flex flex-col-reverse space-y-reverse space-y-1"
                        style={{ minHeight: "20px" }}
                      >
                        {stat.attacked1 > maxHeight ? (
                          <>
                            {Array.from({ length: maxHeight - 1 }, (_, i) => (
                              <div
                                key={`attacked1-${i}`}
                                className="w-4 h-4 bg-green-200 border border-green-400 rounded-full"
                              />
                            ))}
                            <div className="w-4 h-4 flex items-center justify-center text-xs text-green-500 font-bold">
                              ⋯
                            </div>
                          </>
                        ) : (
                          Array.from({ length: stat.attacked1 }, (_, i) => (
                            <div
                              key={`attacked1-${i}`}
                              className="w-4 h-4 bg-green-200 border border-green-400 rounded-full"
                            />
                          ))
                        )}
                      </div>
                      <div className="text-xs text-green-500 mt-2 font-medium">
                        {stat.attacked1}
                      </div>
                      <div className="text-xs text-gray-500">一次</div>
                    </div>
                  )}

                  {/* 已感染柱 (仅模式1) */}
                  {mode === 1 && visibleLines.infected && (
                    <div className="flex flex-col items-center">
                      <div
                        className="flex flex-col-reverse space-y-reverse space-y-1"
                        style={{ minHeight: "20px" }}
                      >
                        {stat.infected > maxHeight ? (
                          <>
                            {Array.from({ length: maxHeight - 1 }, (_, i) => (
                              <div
                                key={`infected-${i}`}
                                className="w-4 h-4 bg-purple-500 border border-purple-700 rounded-full"
                              />
                            ))}
                            <div className="w-4 h-4 flex items-center justify-center text-xs text-purple-500 font-bold">
                              ⋯
                            </div>
                          </>
                        ) : (
                          Array.from({ length: stat.infected }, (_, i) => (
                            <div
                              key={`infected-${i}`}
                              className="w-4 h-4 bg-purple-500 border border-purple-700 rounded-full"
                            />
                          ))
                        )}
                      </div>
                      <div className="text-xs text-purple-500 mt-2 font-medium">
                        {stat.infected}
                      </div>
                      <div className="text-xs text-gray-500">感染</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-4 text-center">
          每个圆圈代表一个人，柱的高度表示该类型的人数{" "}
          {dailyStats.some(
            (s) =>
              Math.max(s.zombies, s.healthy, s.attacked1, s.infected || 0) >
              maxHeight
          ) && "(数量过多时用⋯表示)"}
          {dailyStats.length > 5 && (
            <span className="block mt-1 text-orange-600">
              💡 提示：可以左右滑动查看完整数据
            </span>
          )}
        </div>
      </div>
    );
  };

  const { zombieCount, attacksPerDay } = gameStarted
    ? getZombieStats()
    : { zombieCount: 1, attacksPerDay: 2 };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
        僵尸传播模拟器
      </h1>

      {/* 设置面板 */}
      <div className="mb-6 p-4 bg-gray-100 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">游戏模式：</label>
            <select
              value={mode}
              onChange={(e) => setMode(parseInt(e.target.value))}
              disabled={gameStarted}
              className="w-full p-2 border rounded"
            >
              <option value={1}>模式1：感染者不变僵尸</option>
              <option value={2}>模式2：感染者变僵尸</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">健康人数：</label>
            <input
              type="number"
              value={totalPeople}
              onChange={(e) =>
                setTotalPeople(Math.max(1, parseInt(e.target.value) || 1))
              }
              disabled={gameStarted}
              min="1"
              max="100"
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="flex items-end">
            {!gameStarted ? (
              <button
                onClick={initializeGame}
                className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
              >
                开始游戏
              </button>
            ) : (
              <button
                onClick={resetGame}
                className="w-full bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
              >
                重置游戏
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 游戏状态 */}
      {gameStarted && (
        <>
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-blue-600">{day}</div>
                <div className="text-sm text-gray-600">天数</div>
              </div>
              <div>
                <div className="text-lg font-bold text-red-600">
                  {zombieCount}
                </div>
                <div className="text-sm text-gray-600">僵尸数</div>
              </div>
              <div>
                <div className="text-lg font-bold text-purple-600">
                  {attacksPerDay}
                </div>
                <div className="text-sm text-gray-600">每日攻击次数</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-600">
                  {
                    people.filter(
                      (p) => p.status === "healthy" || p.status === "attacked1"
                    ).length
                  }
                </div>
                <div className="text-sm text-gray-600">未完全感染</div>
              </div>
            </div>
          </div>

          {/* 图例 - 统一使用白色背景+蓝色边框 */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-white border-2 border-blue-500 rounded-full"></div>
                <span>健康人</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-200 border-2 border-green-400 rounded-full"></div>
                <span>被攻击1次</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-700 border-2 border-green-900 rounded-full"></div>
                <span>僵尸</span>
              </div>
              {mode === 1 && (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-purple-500 border-2 border-purple-700 rounded-full"></div>
                  <span>已感染</span>
                </div>
              )}
            </div>
          </div>

          {/* 人员网格 */}
          <div className="mb-6">
            {/* 初始僵尸单独显示 */}
            <div className="text-center mb-4">
              <div className="text-sm text-gray-600 mb-2">初始僵尸</div>
              <div className="flex justify-center">
                {people
                  .filter((p) => p.id === 0)
                  .map((person) => (
                    <div
                      key={person.id}
                      className={`${getPersonClass(person)} ${getSelectedBorder(
                        person.id
                      )}`}
                      title={`ID: ${person.id}, 状态: ${person.status}, 被攻击: ${person.attackCount}次`}
                    >
                      Z
                    </div>
                  ))}
              </div>
            </div>

            {/* 其他人员 */}
            <div className="text-center mb-2">
              <div className="text-sm text-gray-600">村民</div>
            </div>
            <div className="grid grid-cols-10 gap-2 justify-center max-w-2xl mx-auto">
              {people
                .filter((p) => p.id !== 0)
                .map((person) => {
                  const selectedCount = selectedAttacks[person.id] || 0;
                  const totalAttackCount = person.attackCount + selectedCount;
                  const canAttack =
                    person.status !== "zombie" &&
                    (mode !== 1 || person.status !== "infected") &&
                    totalAttackCount < 2;

                  return (
                    <div key={person.id} className="relative">
                      <div
                        className={`${getPersonClass(
                          person
                        )} ${getSelectedBorder(person.id)} ${
                          !canAttack ? "cursor-not-allowed opacity-75" : ""
                        }`}
                        onClick={() => canAttack && toggleTarget(person.id)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (selectedCount > 0) removeAttack(person.id);
                        }}
                        title={`ID: ${person.id}, 状态: ${
                          person.status
                        }, 被攻击: ${
                          person.attackCount
                        }次, 今日选择攻击: ${selectedCount}次 ${
                          canAttack ? "(左键增加攻击，右键减少攻击)" : ""
                        }`}
                      >
                        {person.id}
                      </div>
                      {/* 显示选择的攻击次数 */}
                      {selectedCount > 0 && (
                        <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                          {selectedCount}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* 控制面板 */}
          <div className="mb-4 p-4 bg-gray-100 rounded-lg">
            <div className="text-center mb-3">
              <span className="text-sm text-gray-600">
                已分配攻击次数:{" "}
                {Object.values(selectedAttacks).reduce(
                  (sum, count) => sum + count,
                  0
                )}{" "}
                / {attacksPerDay}
              </span>
              {getAttackableTargets().length === 0 &&
                Object.values(selectedAttacks).reduce(
                  (sum, count) => sum + count,
                  0
                ) < attacksPerDay && (
                  <div className="text-xs text-orange-600 mt-1">
                    ⚠️ 没有更多可攻击目标，可以提前执行攻击
                  </div>
                )}
              {Object.keys(selectedAttacks).length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  攻击分配:{" "}
                  {Object.entries(selectedAttacks)
                    .map(([id, count]) => `${id}号×${count}`)
                    .join(", ")}
                </div>
              )}
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={executeAttacks}
                disabled={gameOver}
                className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 disabled:bg-gray-400"
              >
                执行攻击
              </button>
              <button
                onClick={() => setSelectedAttacks({})}
                disabled={gameOver}
                className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 disabled:bg-gray-400"
              >
                清空选择
              </button>
            </div>
            <div className="text-xs text-center text-gray-500 mt-2">
              提示：左键点击增加攻击，右键点击减少攻击
            </div>
          </div>

          {/* 消息显示 */}
          {message && (
            <div className="mb-4 p-3 bg-blue-100 border border-blue-300 rounded-lg text-center">
              <div className="whitespace-pre-line">{message}</div>
            </div>
          )}

          {/* 数据统计 */}
          {dailyStats.length > 1 && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">数据统计</h3>
                <div className="flex gap-2">
                  <select
                    value={chartMode}
                    onChange={(e) => setChartMode(e.target.value)}
                    className="bg-white border border-gray-300 rounded px-3 py-1 text-sm"
                  >
                    <option value="line">折线图</option>
                    <option value="bar">柱状图</option>
                    <option value="stack">圆圈柱状图</option>
                    <option value="table">数据表格</option>
                  </select>
                </div>
              </div>

              {chartMode !== "table" && (
                /* 数据线选择 */
                <div className="mb-4 p-3 bg-white rounded border">
                  <div className="text-sm font-medium mb-2">选择显示数据：</div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleLines.zombies}
                        onChange={() => toggleDataLine("zombies")}
                        className="rounded border-gray-300"
                      />
                      <div className="w-3 h-3 bg-green-700 border border-green-900 rounded"></div>
                      <span className="text-sm">僵尸</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleLines.healthy}
                        onChange={() => toggleDataLine("healthy")}
                        className="rounded border-gray-300"
                      />
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-sm">健康人</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleLines.attacked1}
                        onChange={() => toggleDataLine("attacked1")}
                        className="rounded border-gray-300"
                      />
                      <div className="w-3 h-3 bg-green-200 border border-green-400 rounded"></div>
                      <span className="text-sm">被攻击1次</span>
                    </label>
                    {mode === 1 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={visibleLines.infected}
                          onChange={() => toggleDataLine("infected")}
                          className="rounded border-gray-300"
                        />
                        <div className="w-3 h-3 bg-purple-500 border border-purple-700 rounded"></div>
                        <span className="text-sm">已感染</span>
                      </label>
                    )}
                  </div>
                </div>
              )}

              {chartMode === "line" && (
                /* 折线图 - 健康人使用蓝色实心 */
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {visibleLines.zombies && (
                        <Line
                          type="monotone"
                          dataKey="zombies"
                          stroke="#15803d"
                          strokeWidth={2}
                          name="僵尸"
                        />
                      )}
                      {visibleLines.healthy && (
                        <Line
                          type="monotone"
                          dataKey="healthy"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          name="健康人"
                        />
                      )}
                      {visibleLines.attacked1 && (
                        <Line
                          type="monotone"
                          dataKey="attacked1"
                          stroke="#10b981"
                          strokeWidth={2}
                          name="被攻击1次"
                        />
                      )}
                      {mode === 1 && visibleLines.infected && (
                        <Line
                          type="monotone"
                          dataKey="infected"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          name="已感染"
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {chartMode === "bar" && (
                /* 柱状图 - 健康人使用蓝色实心填充 */
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {visibleLines.zombies && (
                        <Bar dataKey="zombies" fill="#15803d" name="僵尸" />
                      )}
                      {visibleLines.healthy && (
                        <Bar dataKey="healthy" fill="#3b82f6" name="健康人" />
                      )}
                      {visibleLines.attacked1 && (
                        <Bar
                          dataKey="attacked1"
                          fill="#10b981"
                          name="被攻击1次"
                        />
                      )}
                      {mode === 1 && visibleLines.infected && (
                        <Bar dataKey="infected" fill="#8b5cf6" name="已感染" />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {chartMode === "stack" &&
                /* 圆圈柱状图 */
                renderCircleBarChart()}

              {chartMode === "table" /* 表格显示 */ && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-300">
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="border border-gray-300 px-2 py-1">
                          天数
                        </th>
                        <th className="border border-gray-300 px-2 py-1">
                          僵尸
                        </th>
                        <th className="border border-gray-300 px-2 py-1">
                          健康人
                        </th>
                        <th className="border border-gray-300 px-2 py-1">
                          被攻击1次
                        </th>
                        {mode === 1 && (
                          <th className="border border-gray-300 px-2 py-1">
                            已感染
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {dailyStats.map((stat) => (
                        <tr
                          key={stat.day}
                          className={stat.day === day ? "bg-yellow-100" : ""}
                        >
                          <td className="border border-gray-300 px-2 py-1 text-center">
                            {stat.day}
                          </td>
                          <td className="border border-gray-300 px-2 py-1 text-center">
                            {stat.zombies}
                          </td>
                          <td className="border border-gray-300 px-2 py-1 text-center">
                            {stat.healthy}
                          </td>
                          <td className="border border-gray-300 px-2 py-1 text-center">
                            {stat.attacked1}
                          </td>
                          {mode === 1 && (
                            <td className="border border-gray-300 px-2 py-1 text-center">
                              {stat.infected}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 历史记录 */}
          {historyLog.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">历史记录</h3>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {historyLog.map((log, index) => (
                  <div
                    key={index}
                    className="text-sm p-2 bg-white rounded border"
                  >
                    <span className="font-medium text-blue-600">
                      第{log.day}天
                    </span>
                    <span className="mx-2">•</span>
                    <span className="text-gray-700">{log.action}</span>
                    {log.details && (
                      <>
                        <span className="mx-2">:</span>
                        <span className="text-gray-600">{log.details}</span>
                      </>
                    )}
                    {log.result && (
                      <>
                        <span className="mx-2">→</span>
                        <span className="text-green-600">{log.result}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default ZombieGameSimulator;
